import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { TypeChecker } from '@attain-apps/type-guards'

import * as state from '../../../../../../../state'
import * as shared from '../../../shared'

const SPACE_DIMENSION_ID = ents.core.dms.models.Dimension.Id.from(
  ents.extra.quiver.resources.data.dimensions.space.RESOURCE.payload,
)

const BUILDING_ENTITY_ID =
  ents.extra.quiver.resources.data.dimensions.space.entities.building.MODEL_ID

const DEPARTMENT_ENTITY_ID =
  ents.extra.quiver.resources.data.dimensions.organization.entities.department
    .MODEL_ID

/** Modal for assigning existing lab space records to a lab service. */
@ngCore.Component({
  imports: [
    ds.button.Component,
    ds.input.checkbox.Component,
    ds.input.select.multiple.Component,
    ds.layout.widget.Component,
    ds.loader.Component,
    ds.overlay.centered.Component,
    ds.tag.Component,
    shared.dimensionFilter.Component,
  ],
  selector: 'atn-space-assign-labs-to-lab-service-modal',
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.space.full.recordAssignableLabs.Collection} */
  public readonly assignableEc = ngCore.inject(
    state.space.full.recordAssignableLabs.Collection,
  )

  /** @see {@link state.space.full.recordLabAssignments.Collection} */
  public readonly assignedEc = ngCore.inject(
    state.space.full.recordLabAssignments.Collection,
  )

  /** @see {@link state.dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(state.dms.schema.Feature)

  /** @see {@link state.space.organizations.Feature} */
  private readonly _organizationsFeature = ngCore.inject(
    state.space.organizations.Feature,
  )

  /** @see {@link state.space.full.recordAssignableLabs.plugins.spaceDimensionFilter.Plugin} */
  public readonly dimensionFilterPlugin = this.assignableEc.plugins
    .lookup(
      state.space.full.recordAssignableLabs.plugins.spaceDimensionFilter.Plugin,
    )
    .get()

  /** @see {@link state.space.full.recordAssignableLabs.plugins.responsiblePersonFilter.Plugin} */
  public readonly responsiblePersonFilterPlugin = this.assignableEc.plugins
    .lookup(
      state.space.full.recordAssignableLabs.plugins.responsiblePersonFilter
        .Plugin,
    )
    .get()

  /** @see {@link state.space.full.recordAssignableLabs.plugins.clusterDimensionFilter.Plugin} */
  public readonly clusterDimensionFilterPlugin = this.assignableEc.plugins
    .lookup(
      state.space.full.recordAssignableLabs.plugins.clusterDimensionFilter
        .Plugin,
    )
    .get()

  /** Department options for the department filter, from all departments. */
  public readonly departmentOptionsSg = ngCore.computed(
    (
      allDepartmentEc = this._organizationsFeature.selectors.allDepartmentEc.asSignal(),
    ) =>
      allDepartmentEc?.plugins.cachePlugin.feature.selectors
        .getCachedEntities()
        .asSignal()
        .filter(TypeChecker.isNotNullish.raw)
        .map(org => ({ label: org.name, value: org.id })) ?? [],
  )

  /** Current organization (department) filter, from the applied filters. */
  public readonly orgFilterSg = ngCore.computed(
    (
      filters = this.assignableEc.plugins.currentListPlugin.appliedParams.selectors.filters.asSignal(),
    ) => filters?.orgFilter,
  )

  /** Records available for selection (not yet assigned) */
  public readonly assignableRecordsSg = ngCore.computed(
    (
      allRecords = this.assignableEc.plugins.currentListPlugin.feature.selectors.entities.asSignal(),
      labAssignments = this.assignedEc.plugins.currentListPlugin.feature.selectors.entities.asSignal(),
      schema = this._schemaFeature.selectors.schema.asSignal(),
    ) => {
      const leafEntityId = schema.tryGetLeafEntityOf(SPACE_DIMENSION_ID)?.id
      if (!leafEntityId) {
        return allRecords
      }

      // Each assignment's lab id comes from its `spaceRecordLookup` (its own
      // `.id` isn't the lab's).
      const assignedIds = new Set(
        labAssignments
          .map(
            labAssignment => labAssignment.spaceRecordLookup[leafEntityId]?.id,
          )
          .filter(TypeChecker.isNotNullish.raw),
      )
      return allRecords.filter(record => !assignedIds.has(record.id))
    },
  )

  /** Currently selected records */
  public readonly selectedRecordsSg = ngCore.computed(
    (
      selected = this.assignableEc.plugins.selectionPlugin.feature.selectors
        .getAllSelected({
          from: 'applied',
          target: this.assignedEc.associations?.selectionTarget,
        })
        .asSignal(),
    ) => selected.filter(TypeChecker.isNotNullish.raw),
  )

  /** Section title — leaf entity name with selected count when any are checked (e.g. "Room (3)") */
  public readonly recordTitleSg = ngCore.computed(
    (
      schema = this._schemaFeature.selectors.schema.asSignal(),
      selectedCount = this.selectedRecordsSg().length,
    ) => {
      const label =
        schema.tryGetLeafEntityOf(SPACE_DIMENSION_ID)?.name ?? 'Records'
      return selectedCount > 0 ? `${label} (${selectedCount})` : label
    },
  )

  /** Whether a given record is currently selected */
  public isRecordSelected(id: ents.extra.space.Record.Id): boolean {
    return this.selectedRecordsSg().some(r => r.id === id)
  }

  /**
   * Returns "BuildingName - RecordName" (e.g. "Harris Hall - 114").
   * Falls back to just the record name if the building entity is unknown.
   */
  public getRecordLabel(record: ents.extra.space.Record): string {
    const buildingName = record.ancestors[BUILDING_ENTITY_ID]?.name
    return buildingName ? `${buildingName} - ${record.name}` : record.name
  }

  /**
   * Updates the department filter, preserving any space-dimension selection.
   * Written to the edited draft so it applies through the same cycle as the
   * space filter (which keeps the two from overwriting each other).
   */
  public onDepartmentChange(
    departmentIds: readonly (ents.extra.space.Organization.Id | undefined)[],
  ): void {
    const filters =
      this.assignableEc.plugins.currentListPlugin.appliedParams.selectors.filters.asSignal()
    this.assignableEc.plugins.currentListPlugin.editedParams.actions.setFilters
      .from('AssignLabsToLabService.Modal.DepartmentFilter.Changed')
      .dispatch({
        ...filters,
        orgFilter: departmentIds.length
          ? {
              entityId: DEPARTMENT_ENTITY_ID,
              recordIds: departmentIds.filter(TypeChecker.isNotNullish.raw),
            }
          : undefined,
      })
  }

  /** Toggles a record checkbox */
  public recordCheckboxChangedHandler(
    id: ents.extra.space.Record.Id,
    selected: boolean,
  ): void {
    this.assignableEc.plugins.selectionPlugin.applied.actions.putInSelectionLookup
      .from('AssignLabsToLabService.Modal.RecordCheckbox.Toggled')
      .dispatch({
        key: this.assignedEc.associations?.selectionTarget,
        value: selected ? { add: id } : { remove: id },
      })
  }

  /** Removes a record from the selection */
  public removeSelectedRecord(id: ents.extra.space.Record.Id): void {
    this.assignableEc.plugins.selectionPlugin.applied.actions.putInSelectionLookup
      .from('AssignLabsToLabService.Modal.SelectedTag.Removed')
      .dispatch({
        key: this.assignedEc.associations?.selectionTarget,
        value: { remove: id },
      })
  }

  /** Saves the selected records */
  public saveClickHandler(): void {
    this.assignedEc.plugins.editingPlugin.feature.actions.submit
      .from('AssignLabsToLabService.Modal.Save.Clicked')
      .dispatch({ create: true })
  }

  /** Cancels without saving */
  public cancelClickHandler(): void {
    this.assignedEc.plugins.editingPlugin.feature.actions.discard
      .from('AssignLabsToLabService.Modal.Cancel.Clicked')
      .dispatch({ create: true })
  }
}
